/**
 * A drawn run with a BEND through an island leg, filled through ONE corner
 * plan (angled-runs design 2026-11-03,
 * claude-docs/2026-11-03-angled-cabinet-runs-design.md).
 *
 * Every free-standing leg used to be filled on its own, so a bend between two
 * legs — or between a wall leg and a leg leaving it — had no corner at all:
 * the boxes overlapped where the cabinets were on the inside of the turn and
 * a wedge opened where they were outside, each leg under its own rectangle
 * of countertop. Rather than teach the free-standing fill corners of its own,
 * a run with a joined free bend hands its island legs to the WALL path's one
 * `fillRun` call as synthesized virtual walls (`bentRunSegments`), on the
 * run's one carried side (`runSide.js`), marked `drawnFreeLeg`. The bends then
 * get the corner every wall corner gets — the oblique stop and filler at any
 * angle, the wall-corner choices at a right angle, the reveal, the clearing of
 * an appliance standing at the vertex — and indices stay the STORED ones, so
 * bays and corner choices keep meaning what they mean.
 *
 * What makes an island leg an island leg — its finished back, the counter
 * reaching past its line to the island's outer edge, the reveal at an open
 * end, a back row, stools — is then added per leg by the free-standing fill
 * itself, handed the front row this pass built (`finishBentLegs`). The one
 * piece neither pass builds is the counter a bend leaves open — outside a
 * bend toward the cabinets, where the legs' outward strips meet; at the point
 * of a bend away from them (`bendCounterPatches`).
 *
 * A run with both a wall leg and an island leg comes here too, joined or not
 * (`materializeRun`), so its legs, corners and bays keep their STORED numbers.
 * Every lone island, every wall-only run and every generated and scanned run
 * never reaches this module.
 *
 * Pure. No I/O.
 */

const polygonClipping = require('polygon-clipping');
const {
  isFillableTurn,
  isRightTurn,
  sameCabinetSide,
  bendAt,
  legDirection,
  legEndpoints,
  legLength,
  lineCrossing,
  turnDeg,
} = require('./bendGeometry');
const { profileOf, counterTopOffsetFor } = require('./profiles');
const { isOutsideBend, frontPoint } = require('./outsideBend');
const {
  synthesizeVirtualWall,
  resolveSegmentDepths,
  defaultSidedness,
  fillFreestandingSegment,
  freeLegTags,
} = require('./freestanding');
const { freeLegCookSides } = require('./runSide');
const {
  getWallAxes,
  worldToWallParam,
} = require('../kitchen-assembly/wallMath');
const { planOutline } = require('../geometry/objectFootprint');
const { polygonArea } = require('../geometry/floorPolygon');
const { doPolygonsOverlap } = require('../bathroom-analyzer/pipeline/geometry');
const { fillRun } = require('./fillRun');
const { withSnapRetry } = require('../geometry/clipRetry');
const { legsAtCorner, seamObliqueCorner } = require('./cornerSlabSeams');

const EPS = 1e-6;

/** A bend the fill can plan: joined, through a free leg, 5°–90°. */
function isFillableFreeBend(b) {
  return Boolean(b && b.free && b.joined && isFillableTurn(b.turnDeg));
}

/**
 * The same, between two legs of the COMBINED pass (they carry walls), where
 * the cabinets also stay on one side of the line — a wall leg whose room side
 * is the other side makes no corner (`corners.isCorner`).
 */
const isPlannedBend = (a, b) =>
  isFillableFreeBend(bendAt(a, b)) && sameCabinetSide(a, b);

/** Does this HYDRATED run have a bend the combined fill should plan? */
function hasJoinedFreeBend(run) {
  const segs = run?.segments || [];
  for (let i = 0; i < segs.length - 1; i += 1) {
    if (isFillableFreeBend(bendAt(segs[i], segs[i + 1]))) return true;
  }
  return false;
}

/**
 * The run's segments for ONE fill pass, in stored order: wall legs as they
 * are; each free leg a virtual wall on the run's side, travelling start → end
 * (so a bend's shared vertex is where the corner plan looks for it), its
 * front row's depth on the segment (`segmentCabinetDepth`), and the leg as
 * resolved (`_freeLeg`) for the finishing pass.
 */
function bentRunSegments(run, { otherCabinetRuns = [] } = {}) {
  const freeSegs = (run.segments || []).filter((s) => s.freestanding);
  const sides = freeLegCookSides(run, { otherCabinetRuns });
  return (run.segments || []).map((seg, i) => {
    if (!seg.freestanding) return seg;
    const idx = seg._storedIdx ?? i;
    const cookSide = sides.get(idx);
    const sided =
      seg.sided ?? defaultSidedness(seg, freeSegs, otherCabinetRuns).sided;
    const wall = {
      ...synthesizeVirtualWall(seg.start, seg.end, {
        side: cookSide,
        id: `${run.id}_freestanding_${idx}`,
      }),
      drawnFreeLeg: true,
    };
    return {
      wall,
      startParam: worldToWallParam(wall, seg.start),
      endParam: worldToWallParam(wall, seg.end),
      cabinetDepth: resolveSegmentDepths({ ...seg, sided }).front,
      _storedIdx: idx,
      _freeLeg: { ...seg, sided, cookSide },
    };
  });
}

const firstWallId = (o) => (o?.parentWallIds || [])[0];
// `buildCountertopRuns` names a slab after the wall it stands on and carries
// no wall reference of its own.
const isCounterOn = (o, wallId) =>
  o?.type === 'countertop' &&
  typeof o.id === 'string' &&
  o.id.startsWith(`countertop_${wallId}_`);
const ROW_TYPES = new Set(['lower-cabinet', 'microwave']);
const opposite = (side) => (side === 'right' ? 'left' : 'right');

/**
 * The back rows of the run's two-sided legs, filled in ONE pass on the
 * opposite side with a corner wherever two of them meet — an inside bend of
 * the front rows is an outside bend of the back rows, and the corner plan
 * reads that off the walls. The rows' own slabs are not kept: one counter per
 * leg spans both rows, as on a straight two-sided leg.
 *
 * @returns {{ byLeg: Map<number, Object>, cornerPieces: Object[] }} each
 *   two-sided leg's back row (the shape `fillFreestandingSegment` reads as
 *   `ctx.backRow`), null for a two-sided leg with only a finished panel
 */
function fillBackRows(run, segments, fillCtx) {
  const byLeg = new Map();
  const backSegs = [];
  for (const seg of segments) {
    const leg = seg?._freeLeg;
    if (!leg || leg.sided !== 'double') continue;
    const depths = resolveSegmentDepths(leg);
    if (!(depths.back > 0)) {
      byLeg.set(seg._storedIdx, null);
      continue;
    }
    const wall = {
      ...synthesizeVirtualWall(leg.start, leg.end, {
        side: opposite(leg.cookSide),
        id: `${seg.wall.id}_back`,
      }),
      drawnFreeLeg: true,
    };
    backSegs.push({
      wall,
      startParam: worldToWallParam(wall, leg.start),
      endParam: worldToWallParam(wall, leg.end),
      cabinetDepth: depths.back,
      _storedIdx: seg._storedIdx,
    });
  }
  if (!backSegs.length) return { byLeg, cornerPieces: [] };
  const out = fillRun(
    {
      ...run,
      id: `${run.id}_back`,
      segments: backSegs,
      bays: [],
      bayOverrides: [],
      uppers: false,
    },
    {
      floorY: fillCtx.floorY,
      roomAppearance: fillCtx.roomAppearance,
      openings: [],
      walls: [],
      cabinetDepth: backSegs[0].cabinetDepth,
    }
  );
  const fillerIds = new Set((out.fillers || []).map((o) => o.id));
  for (const seg of backSegs) {
    const on = (o) => firstWallId(o) === seg.wall.id;
    byLeg.set(seg._storedIdx, {
      cabinets: (out.cabinets || []).filter(
        (o) => on(o) && !fillerIds.has(o.id)
      ),
      fillers: (out.fillers || []).filter(on),
      cornerUnits: [],
      microwaves: [],
      countertops: [],
      appliancePlacements: [],
      warnings: [],
    });
  }
  const cornerPieces = (out.cornerUnits || [])
    .filter((o) => !o.dimensions?.isUpper)
    .map((o) => ({ ...o, runId: run.id }));
  return { byLeg, cornerPieces };
}

const STOOL_BLOCKERS = new Set([
  'lower-cabinet',
  'corner-cabinet',
  'corner-fill-cabinet',
  'blank-panel',
]);

/**
 * Where two seated legs meet inside a turn their stools converge; a stool
 * that would stand in another leg's stool or cabinetry is not placed (rather
 * than placed where no one could sit).
 */
function dropCollidingStools(objects) {
  const blockers = objects
    .filter((o) => STOOL_BLOCKERS.has(o.type))
    .map((o) => ({ o, poly: planOutline(o) }))
    .filter((b) => b.poly);
  const kept = [];
  const dropped = new Set();
  for (const s of objects.filter((o) => o.type === 'bar-stool')) {
    const poly = planOutline(s);
    if (!poly) continue;
    const hits = (b) => doPolygonsOverlap(poly, b.poly, 0.002);
    const blocked =
      blockers.some((b) => b.o.freeSegIndex !== s.freeSegIndex && hits(b)) ||
      kept.some((k) => k.o.freeSegIndex !== s.freeSegIndex && hits(k));
    if (blocked) dropped.add(s.id);
    else kept.push({ o: s, poly });
  }
  return dropped.size ? objects.filter((o) => !dropped.has(o.id)) : objects;
}

/**
 * Hand each island leg its slice of the combined pass and let the
 * free-standing fill finish it — the leg's counter grown to the island's
 * outer edge, its finished back, its back row and stools — exactly as it
 * finishes a leg it filled itself. The leg's appliances stay where the
 * combined pass emitted them (by the run's emit policy), tagged as the leg's.
 *
 * @returns {{ objects: Object[], warnings: Object[], backCabinetCount: number,
 *   backCornerCount: number }} — the back rows' boxes and corner units, which
 *   the run's own counts (the wall pass's) do not hold
 */
function finishBentLegs({ run, segments, objects, out, fillCtx }) {
  const fillerIds = new Set((out.fillers || []).map((o) => o.id));
  const placementIds = new Set(
    (out.appliancePlacements || []).map((o) => o.id)
  );
  let kept = [...objects];
  const warnings = [];
  const backRows = fillBackRows(run, segments, fillCtx);
  kept.push(...backRows.cornerPieces);
  for (const seg of segments) {
    const leg = seg?._freeLeg;
    if (!leg) continue;
    const wallId = seg.wall.id;
    const mine = (o) => firstWallId(o) === wallId || isCounterOn(o, wallId);
    const rows = kept.filter(
      (o) => mine(o) && (ROW_TYPES.has(o.type) || o.type === 'countertop')
    );
    kept = kept.filter((o) => !rows.includes(o));
    const tags = freeLegTags(leg, seg._storedIdx);
    // the leg's appliances, as the combined pass emitted them
    kept = kept.map((o) =>
      placementIds.has(o.id) && firstWallId(o) === wallId
        ? { ...o, ...tags }
        : o
    );
    const isFiller = (o) => fillerIds.has(o.id);
    const frontRow = {
      cabinets: rows.filter((o) => o.type === 'lower-cabinet' && !isFiller(o)),
      fillers: rows.filter((o) => o.type === 'lower-cabinet' && isFiller(o)),
      cornerUnits: [],
      microwaves: rows.filter((o) => o.type === 'microwave'),
      countertops: rows.filter((o) => o.type === 'countertop'),
      appliancePlacements: [],
      warnings: [],
      zones: [],
      bays: [],
    };
    const out = fillFreestandingSegment(leg, {
      floorY: fillCtx.floorY,
      ceilingY: fillCtx.ceilingY,
      ceilingId: fillCtx.ceilingId,
      roomAppearance: fillCtx.roomAppearance,
      openings: [],
      walls: [],
      neighbours: fillCtx.neighbours,
      run,
      segmentId: wallId,
      frontRow,
      ...(backRows.byLeg.has(seg._storedIdx)
        ? { backRow: backRows.byLeg.get(seg._storedIdx) }
        : {}),
    });
    kept.push(...(out.objects || []).map((o) => ({ ...o, ...tags })));
    warnings.push(...(out.warnings || []));
  }
  const backRowList = [...backRows.byLeg.values()].filter(Boolean);
  return {
    objects: fitBackPanelsAtBends(segments, dropCollidingStools(kept)),
    warnings,
    backCabinetCount: backRowList.reduce((n, r) => n + r.cabinets.length, 0),
    backCornerCount: backRows.cornerPieces.length,
  };
}

const isBackPanel = (o) =>
  o?.type === 'blank-panel' && o.category?.backPanel === true;

/** A panel box lengthened (`by` > 0) or shortened at the end toward `toward`. */
function resizePanelEnd(panel, toward, by) {
  return {
    ...panel,
    dimensions: { ...panel.dimensions, x: panel.dimensions.x + by },
    center: {
      ...panel.center,
      x: panel.center.x + (toward.x * by) / 2,
      z: panel.center.z + (toward.z * by) / 2,
    },
  };
}

/**
 * Each island leg's finished back panel stops at its own line's end, so at a
 * bend the two panels leave a notch on the outside of the turn (a bend toward
 * the cabinets, where the backs diverge) or stack on the inside (a bend away
 * from them, where they converge). Toward the cabinets each panel runs on
 * `thickness · tan(turn / 2)` so their outer faces meet at the mitre;
 * away from them leg A's panel stops where it would enter leg B's.
 */
function fitBackPanelsAtBends(segments, objects) {
  let out = objects;
  for (let i = 0; i < segments.length - 1; i += 1) {
    const a = segments[i];
    const b = segments[i + 1];
    if (!a?.wall?.drawnFreeLeg || !b?.wall?.drawnFreeLeg) continue;
    if (!isPlannedBend(a, b)) continue;
    const panelOf = (seg) =>
      out.find((o) => isBackPanel(o) && o.freeSegIndex === seg._storedIdx);
    const pA = panelOf(a);
    const pB = panelOf(b);
    if (!pA || !pB) continue;
    const dA = legDirection(a);
    const dB = legDirection(b);
    const byId = new Map();
    if (!isOutsideBend(a, b)) {
      const half = (Math.abs(bendAt(a, b).turnDeg) * Math.PI) / 360;
      byId.set(pA.id, resizePanelEnd(pA, dA, pA.dimensions.z * Math.tan(half)));
      byId.set(
        pB.id,
        resizePanelEnd(pB, mul(dB, -1), pB.dimensions.z * Math.tan(half))
      );
    } else {
      const V = bendAt(a, b).vertex;
      const overlap = clipRings('intersection', planOutline(pA), [
        planOutline(pB),
      ]);
      // how far back along leg A, from the vertex, its panel enters B's
      const reach = Math.max(
        0,
        ...overlap.flat().map((p) => dot(sub(p, V), mul(dA, -1)))
      );
      if (reach > EPS) byId.set(pA.id, resizePanelEnd(pA, dA, -reach - EPS));
    }
    out = out.map((o) => byId.get(o.id) || o);
  }
  return out;
}

const sub = (a, b) => ({ x: a.x - b.x, z: a.z - b.z });
const add = (a, b) => ({ x: a.x + b.x, z: a.z + b.z });
const mul = (a, s) => ({ x: a.x * s, z: a.z * s });
const dot = (a, b) => a.x * b.x + a.z * b.z;

const ring = (poly) => [...poly.map((p) => [p.x, p.z]), [poly[0].x, poly[0].z]];

/** `polygon-clipping` with the snap retry; the outer rings it returns. */
function clipRings(op, subject, others) {
  const res = withSnapRetry((snap) => {
    const s = (poly) => [poly.map(([x, z]) => [snap(x), snap(z)])];
    return polygonClipping[op](
      s(ring(subject)),
      ...others.map((o) => s(ring(o)))
    );
  });
  return (res?.value || [])
    .filter((mp) => mp[0]?.length >= 4)
    .map((mp) => mp[0].slice(0, -1).map(([x, z]) => ({ x, z })));
}

/**
 * The band of counter a bend leaves open, before what is already there is
 * taken out. Toward the cabinets the open side is OUTSIDE the turn, where the
 * legs' outward strips (the finished back and its overhang) meet at a mitre;
 * away from them it is the FRONT, where the wedge opens and the counter comes
 * to a point (user, 2026-11-03).
 */
function openBand({ V, A, B }, outside) {
  const side = (leg) => (outside ? leg.front : -leg.back);
  if (!outside && A.back < EPS && B.back < EPS) return null;
  const offA = add(V, mul(A.n, side(A)));
  const offB = add(V, mul(B.n, side(B)));
  // At the point of a bend away from the cabinets the front edges meet where
  // `frontPoint` says, or the band closes straight between the corners.
  const meet = outside
    ? frontPoint(offA, mul(A.away, -1), offB, B.away)
    : lineCrossing(offA, A.away, offB, B.away);
  if (!meet && !outside) return null;
  const band = distinct([
    add(V, mul(A.away, A.reach)),
    add(offA, mul(A.away, A.reach)),
    ...(meet ? [meet] : []),
    add(offB, mul(B.away, B.reach)),
    add(V, mul(B.away, B.reach)),
    V,
  ]);
  return band.length >= 3 ? band : null;
}

/** Which side of the line a → b point `p` is on (sign of the cross product). */
const sideOfLine = (a, b, p) =>
  Math.sign((b.x - a.x) * (p.z - a.z) - (b.z - a.z) * (p.x - a.x));

/**
 * The front of a bend toward the cabinets, from each leg's counter end to the
 * vertex — the slab over a filler that is not the generator's kite (a zero
 * stop). Its front comes to where the two legs' FRONTS cross, as every inside
 * corner of a counter does: the deeper leg's front carries on past its end
 * until it meets the other's. Closed straight from one slab's front corner to
 * the other's instead, it cut that corner off — the diagonal's front stood
 * 0.4" short of the 36" leg's front line (the user, 2026-11-03). Straight
 * only where the fronts cross on the vertex's side of that line, or never.
 */
function frontChord({ V, A, B }) {
  const cornerA = add(add(V, mul(A.away, A.reach)), mul(A.n, A.front));
  const cornerB = add(add(V, mul(B.away, B.reach)), mul(B.n, B.front));
  const meet = lineCrossing(cornerA, A.away, cornerB, B.away);
  const pointed =
    meet &&
    sideOfLine(cornerA, cornerB, meet) * sideOfLine(cornerA, cornerB, V) < 0;
  const band = distinct([
    V,
    add(V, mul(A.away, A.reach)),
    cornerA,
    ...(pointed ? [meet] : []),
    cornerB,
    add(V, mul(B.away, B.reach)),
  ]);
  return band.length >= 3 ? band : null;
}

/**
 * At a bend AWAY from the cabinets the legs' backs converge, so their outward
 * strips cross at the inside of the turn — and the leg that runs through to
 * the outside corner carries its strip past the vertex, over the other leg's
 * first box. The leg that runs through (else leg B) gives that up: its counter
 * keeps its rectangle's centre, size and turn and is OUTLINED by what is left
 * (`polygonCorners`, as an angled end's counter is).
 */
function clipAtOutsideBend(a, b, legs, objects) {
  // the through leg of a right-angled outside bend (`planOutsideBend`)
  const loserIsA =
    isRightTurn(turnDeg(legDirection(a), legDirection(b))) &&
    legLength(a) >= legLength(b);
  const losers = loserIsA ? legs.cA : legs.cB;
  const winners = (loserIsA ? legs.cB : legs.cA)
    .map(planOutline)
    .filter(Boolean);
  if (!winners.length) return objects;
  const byId = new Map();
  for (const ct of losers) {
    const poly = planOutline(ct);
    if (!poly) continue;
    const overlap = clipRings('intersection', poly, winners);
    if (!overlap.some((p) => polygonArea(p) > 1e-6)) continue;
    const kept = clipRings('difference', poly, winners).sort(
      (p, q) => polygonArea(q) - polygonArea(p)
    )[0];
    if (kept) byId.set(ct.id, { ...ct, polygonCorners: kept });
  }
  return byId.size ? objects.map((o) => byId.get(o.id) || o) : objects;
}

/**
 * The countertop at every bend through an island leg: the band the bend
 * leaves open (`openBand`) is built as what is left once every counter
 * already there is taken out (`polygon-clipping`), so a piece can never stack
 * on a slab; at a bend away from the cabinets the legs' crossing strips are
 * clipped first (`clipAtOutsideBend`). At a bend toward them `fillRun` has
 * already ended the legs' slabs where their fronts cross
 * (`cornerSlabSeams.js`), but before the island fill grew an island leg's
 * slab to its outer edge — so the kite's slab is rebuilt here with the legs'
 * final reach, at the cabinet stops `fillRun` returned (`kiteStops`).
 *
 * `ctx` is the fill's context (`fillRun`'s), which the kite is rebuilt with;
 * without it, or without its stops, the kite is left as `fillRun` built it.
 *
 * @returns {Object[]} the run's objects, its counters clipped where they
 *   crossed, plus one countertop piece (`polygonCorners`) per open region
 */
function bendCounterPatches({
  run,
  segments,
  objects,
  floorY,
  ctx = null,
  kiteStops = [],
}) {
  let out = objects;
  const patches = [];
  for (let i = 0; i < segments.length - 1; i += 1) {
    const a = segments[i];
    const b = segments[i + 1];
    if (!isPlannedBend(a, b)) continue;
    const outside = isOutsideBend(a, b);
    const countersNow = () =>
      out.filter((o) => /countertop/.test(o?.type || ''));
    const stops = kiteStops.find((k) => k.cornerIndex === i);
    const crossing = outside && legsAtCorner(a, b, countersNow());
    if (crossing) out = clipAtOutsideBend(a, b, crossing, out);
    else if (!outside && stops)
      out = seamObliqueCorner({
        run,
        segments,
        cornerIndex: i,
        stopA: stops.stopA,
        stopB: stops.stopB,
        objects: out,
        floorY,
        ctx,
      });
    const counters = countersNow();
    if (!counters.length) continue;
    const legs = legsAtCorner(a, b, counters);
    if (!legs?.A || !legs?.B) continue;
    // Toward the cabinets, the front of the bend too: where one leg stops at
    // the vertex (`corners.zeroStopFiller`) no kite slab covers its filler,
    // and everywhere else the slabs already there leave nothing of it.
    const bands = [
      openBand(legs, outside),
      outside ? null : frontChord(legs),
    ].filter(Boolean);
    if (!bands.length) continue;
    const ref = legs.cA[0] || legs.cB[0] || counters[0];
    const taken = counters.map(planOutline).filter(Boolean);
    const pieces = bands.flatMap((band) =>
      clipRings('difference', band, taken)
    );
    pieces.forEach((outer, k) => {
      // a sliver the two slabs already share is no piece of counter
      if (polygonArea(outer) < 1e-4) return;
      patches.push(
        patchCountertop({ run, cornerIndex: i, k, outer, ref, floorY })
      );
    });
  }
  return [...out, ...patches];
}

/** Drop a point that repeats the one before it (a wall leg has no strip). */
function distinct(poly) {
  return poly.filter((p, i) => {
    const q = poly[(i + poly.length - 1) % poly.length];
    return Math.hypot(p.x - q.x, p.z - q.z) > 1e-9;
  });
}

/** A counter piece outlined by `polygonCorners`, at the run's counter height. */
function patchCountertop({ run, cornerIndex, k, outer, ref, floorY }) {
  const xs = outer.map((p) => p.x);
  const zs = outer.map((p) => p.z);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minZ = Math.min(...zs);
  const maxZ = Math.max(...zs);
  // the slab it continues, else the run's own counter
  const thickness = ref.dimensions?.y ?? profileOf(run).counterThickness;
  const y = ref.center?.y ?? floorY + counterTopOffsetFor(run) - thickness / 2;
  return {
    id: `${run.id}_bend_${cornerIndex}_countertop_${k}`,
    // A piece of the run's own countertop (a run-wide finish edit keys on
    // `countertop.runId`), outlined like an angled end's (`polygonCorners`).
    type: 'countertop',
    label: ref.label || 'Countertop',
    // a corner piece: no long axis, so no END for `endFinish` to govern
    // (`countertopEdges.isCornerPiece`)
    category: { countertop: true, freestanding: true, 'corner-piece': true },
    center: { x: (minX + maxX) / 2, y, z: (minZ + maxZ) / 2 },
    dimensions: { x: maxX - minX, y: thickness, z: maxZ - minZ },
    rotation: { x: 0, y: 0, z: 0 },
    polygonCorners: outer.map((p) => ({ x: p.x, z: p.z })),
    addedBy: 'manual-cabinet-run',
    runId: run.id,
    cornerIndex,
    ...(ref.appearance ? { appearance: ref.appearance } : {}),
    ...(ref.texture ? { texture: ref.texture } : {}),
  };
}

module.exports = {
  hasJoinedFreeBend,
  isFillableFreeBend,
  bentRunSegments,
  finishBentLegs,
  bendCounterPatches,
};
